// Image compression utility — runs entirely client-side using <canvas>.
// Target: ~1600px max side, JPEG quality 0.82 — good OCR readability, small payload.

const MAX_SIDE = 1600;
const QUALITY = 0.82;

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      // dataURL = "data:<mime>;base64,<DATA>"
      const result = r.result || "";
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image unreadable"));
    img.src = src;
  });
}

/**
 * Compress a JPG/PNG/WEBP file/blob → returns { base64, mime }.
 * Always outputs JPEG to keep payload small.
 */
export async function compressImageToBase64(fileOrBlob) {
  const url = URL.createObjectURL(fileOrBlob);
  try {
    const img = await loadImage(url);
    let { width, height } = img;
    if (width > MAX_SIDE || height > MAX_SIDE) {
      if (width >= height) {
        height = Math.round((height * MAX_SIDE) / width);
        width = MAX_SIDE;
      } else {
        width = Math.round((width * MAX_SIDE) / height);
        height = MAX_SIDE;
      }
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF"; // flatten transparency
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise((res) =>
      canvas.toBlob((b) => res(b), "image/jpeg", QUALITY)
    );
    const base64 = await blobToBase64(blob);
    return { base64, mime: "image/jpeg" };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Convert first page of a PDF to a compressed JPEG base64.
 * Uses dynamic import of pdfjs-dist (already in node_modules via jsPDF? — fallback to image rejection).
 */
export async function pdfFirstPageToBase64(file) {
  // Lazy-load pdfjs-dist (installed separately).
  const pdfjs = await import("pdfjs-dist/build/pdf");
  // Use the bundled worker via Vite/Webpack-friendly URL
  pdfjs.GlobalWorkerOptions.workerSrc = await import(
    "pdfjs-dist/build/pdf.worker.min.mjs?url"
  ).then((m) => m.default).catch(() =>
    // CDN fallback
    `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
  );

  const buf = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: buf }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 2 }); // ~150 dpi
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport }).promise;
  // Re-compress to fit MAX_SIDE
  const blob = await new Promise((res) =>
    canvas.toBlob((b) => res(b), "image/jpeg", QUALITY)
  );
  return compressImageToBase64(blob);
}

/**
 * High-level helper — accepts any supported document and returns base64+mime ready to send.
 */
export async function documentToBase64(file) {
  const type = (file.type || "").toLowerCase();
  if (type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
    return pdfFirstPageToBase64(file);
  }
  if (type.startsWith("image/")) {
    return compressImageToBase64(file);
  }
  throw new Error("Format non supporté. Utilisez PDF, JPG ou PNG.");
}
